import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  MapPin, 
  Ruler, 
  Calendar, 
  Plus, 
  Home,
  Edit2, 
  Trash2, 
  X, 
  Search,
  ChevronLeft,
  StickyNote,
  Save,
  Key,
  Zap,
  Droplets,
  Wind,
  Layers,
  Waves,
  AlertTriangle,
  Info,
  Flame,
  ParkingCircle,
  ArrowUp,
  Dog,
  Lock,
  Box,
  Battery,
  Sun,
  User,
  Navigation,
  Bed,
  Bath,
  Activity,
  Warehouse,
  HardHat,
  Power,
  Scaling,
  Mountain,
  FileText,
  Thermometer,
  ExternalLink,
  ShieldCheck,
  Loader2,
  AlertCircle,
  Construction,
  Hash,
  Upload,
  GlassWater,
  MessageSquareText,
  Eye,
  ArrowLeft,
  CalendarDays,
  Tag,
  Building,
  ChevronRight,
  Droplet,
  Trash,
  Users,
  UserPlus,
  Mail,
  Phone,
  Check,
  ClipboardList,
  CookingPot,
  Compass,
  ArrowDownToLine,
  Trees,
  CloudRain,
  Link as LinkIcon,
  Wrench,
  Gauge,
  ArrowDownCircle,
  LocateFixed,
  CloudUpload,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import { Location, Contact, Note, NoteCategory, LocationMetadata, Inspection } from '../types';
import { supabase, toSnakeCase, getErrorMessage } from '../lib/supabase';

const FormField = ({ label, type = "text", placeholder = "", value, onChange, options = [], className = "" }: any) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</label>
    {type === "select" ? (
      <select 
        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#606c38] focus:bg-white transition-all" 
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select...</option>
        {options.map((opt: any) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    ) : type === "checkbox" ? (
      <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-all min-h-[50px]">
        <input 
          type="checkbox" 
          className="w-5 h-5 rounded-lg border-slate-300 text-[#606c38] focus:ring-[#606c38]" 
          checked={!!value} 
          onChange={(e) => onChange(e.target.checked)} 
        />
        <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight leading-tight">{placeholder || label}</span>
      </label>
    ) : type === "textarea" ? (
      <textarea
        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#606c38] focus:bg-white transition-all resize-none"
        placeholder={placeholder}
        rows={3}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input 
        type={type} 
        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#606c38] focus:bg-white transition-all" 
        placeholder={placeholder} 
        value={value === undefined || value === null ? '' : value} 
        onChange={(e) => {
          const val = e.target.value;
          onChange(type === 'number' ? (val === '' ? 0 : val) : val);
        }} 
      />
    )}
  </div>
);

interface LocationsProps {
  locations: Location[];
  setLocations: React.Dispatch<React.SetStateAction<Location[]>>;
  contacts: Contact[];
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  categories: NoteCategory[];
  inspections: Inspection[];
  initialLocationId?: string | null;
  onClearNavigation?: () => void;
  onNavigateToInspection?: (id: string) => void;
}

const Locations: React.FC<LocationsProps> = ({ 
  locations, setLocations, contacts, notes, setNotes, categories, inspections, 
  initialLocationId, onClearNavigation, onNavigateToInspection 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directFileInputRef = useRef<HTMLInputElement>(null);
  
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', categoryId: categories[0]?.id || '' });
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [updatingDirectImage, setUpdatingDirectImage] = useState(false);

  useEffect(() => {
    if (initialLocationId) {
      setSelectedLocationId(initialLocationId);
      if (onClearNavigation) onClearNavigation();
    }
  }, [initialLocationId, onClearNavigation]);

  const [formData, setFormData] = useState<Partial<Location & LocationMetadata>>({
    name: '', address: '', city: '', state: '', zip: '', type: 'Property', image: '', zillowUrl: '', squareFeet: 0, yearBuilt: new Date().getFullYear(), contactIds: [],
    propertyOccupied: false, utilitiesOnElectric: true, utilitiesOnWater: true, utilitiesOnGas: true,
    bedroomCount: 0, bathroomCount: 0, numberOfStories: 1, roofMaterial: 'Shingle'
  });

  const filteredLocations = locations.filter(l => 
    (l.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (l.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.city || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const selectedLocation = locations.find(l => l.id === selectedLocationId);
  const locationNotes = notes.filter(n => n.entityType === 'Location' && n.entityId === selectedLocationId);
  const associatedContacts = contacts.filter(c => selectedLocation?.contactIds?.includes(c.id));
  const associatedInspections = inspections
    .filter(i => i.propertyId === selectedLocationId)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleOpenAdd = () => {
    setEditingId(null);
    setImageFile(null);
    setFormData({ name: '', address: '', city: '', state: '', zip: '', type: 'Property', image: '', zillowUrl: '', contactIds: [], squareFeet: 0, yearBuilt: new Date().getFullYear() });
    setIsSidebarOpen(true);
  };

  const handleOpenEdit = (loc: Location, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(loc.id);
    setImageFile(null);
    const { metadata, ...core } = loc;
    setFormData({ ...core, ...(metadata || {}) });
    setIsSidebarOpen(true);
  };

  const handleDeleteLocation = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!id) return;
    const confirmMessage = "Permanently purge this asset from the registry? All associated interaction logs and technical data will be lost.";
    if (!window.confirm(confirmMessage)) return;
    setSaving(true);
    try {
      const loc = locations.find(l => l.id === id);
      if (loc?.image && loc.image.includes('location-media')) {
         const path = loc.image.split('/').pop();
         if (path) await supabase.storage.from('location-media').remove([path]);
      }

      const { error } = await supabase.from('locations').delete().eq('id', id);
      if (error) throw error;
      setLocations(prev => prev.filter(l => l.id !== id));
      if (selectedLocationId === id) setSelectedLocationId(null);
      if (editingId === id) setEditingId(null);
      setIsSidebarOpen(false);
    } catch (err) {
      alert(`Deletion failed: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFormData(prev => ({ ...prev, image: reader.result as string }));
      reader.readAsDataURL(file);
    }
  };

  const handleDirectImageDelete = async () => {
    if (!selectedLocation || !selectedLocationId) return;
    if (!confirm('Permanently purge this cover image from the registry?')) return;
    
    setUpdatingDirectImage(true);
    try {
      if (selectedLocation.image && selectedLocation.image.includes('location-media')) {
        const path = selectedLocation.image.split('/').pop();
        if (path) await supabase.storage.from('location-media').remove([path]);
      }

      const { error } = await supabase.from('locations')
        .update({ image: '' })
        .eq('id', selectedLocationId);
      
      if (error) throw error;

      setLocations(prev => prev.map(l => l.id === selectedLocationId ? { ...l, image: '' } : l));
    } catch (err) {
      alert(`Image removal failed: ${getErrorMessage(err)}`);
    } finally {
      setUpdatingDirectImage(false);
    }
  };

  const handleDirectImageUpdate = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLocationId || !selectedLocation) return;
    
    setUpdatingDirectImage(true);
    try {
      if (selectedLocation.image && selectedLocation.image.includes('location-media')) {
        const oldPath = selectedLocation.image.split('/').pop();
        if (oldPath) await supabase.storage.from('location-media').remove([oldPath]);
      }

      const timestamp = new Date().getTime();
      const fileName = `${timestamp}-${file.name.replace(/\s+/g, '_')}`;
      const { error: uploadError } = await supabase.storage
        .from('location-media')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('location-media').getPublicUrl(fileName);

      const { error: dbError } = await supabase.from('locations')
        .update({ image: publicUrl })
        .eq('id', selectedLocationId);
      
      if (dbError) throw dbError;

      setLocations(prev => prev.map(l => l.id === selectedLocationId ? { ...l, image: publicUrl } : l));
    } catch (err) {
      alert(`Image update failed: ${getErrorMessage(err)}`);
    } finally {
      setUpdatingDirectImage(false);
      if (directFileInputRef.current) directFileInputRef.current.value = '';
    }
  };

  const toggleContactAssociation = (contactId: string) => {
    setFormData(prev => {
      const current = prev.contactIds || [];
      const isSelected = current.includes(contactId);
      return {
        ...prev,
        contactIds: isSelected ? current.filter(id => id !== contactId) : [...current, contactId]
      };
    });
  };

  const handleSaveLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { contactIds, id: existingId, createdAt, metadata: leakedMetadata, image: currentImage, ...rest } = formData;
      
      let finalImageUrl = currentImage || '';

      if (imageFile) {
        const timestamp = new Date().getTime();
        const fileName = `${timestamp}-${imageFile.name.replace(/\s+/g, '_')}`;
        const { error: uploadError } = await supabase.storage
          .from('location-media')
          .upload(fileName, imageFile, { cacheControl: '3600', upsert: false });
        if (uploadError) throw new Error(`Media upload failed: ${uploadError.message}`);
        const { data: { publicUrl } } = supabase.storage.from('location-media').getPublicUrl(fileName);
        finalImageUrl = publicUrl;
      }

      const coreFields = ['name', 'address', 'city', 'state', 'zip', 'type', 'image', 'zillow_url', 'square_feet', 'year_built'];
      const updatedMetadata: any = {};
      const updatedCore: any = { image: finalImageUrl };
      
      const fieldMap: Record<string, string> = {
        name: 'name', address: 'address', city: 'city', state: 'state', zip: 'zip', 
        type: 'type', zillowUrl: 'zillow_url', squareFeet: 'square_feet', yearBuilt: 'year_built'
      };

      Object.keys(rest).forEach(key => {
        const dbKey = fieldMap[key];
        let val = (rest as any)[key];
        if (['squareFeet', 'yearBuilt'].includes(key) && typeof val === 'string') val = parseFloat(val) || 0;
        
        if (dbKey && coreFields.includes(dbKey)) {
          updatedCore[dbKey] = val;
        } else {
          updatedMetadata[key] = val;
        }
      });

      if (!updatedCore.name) {
        updatedCore.name = updatedCore.address ? updatedCore.address.split(',')[0] : 'Unnamed Asset';
      }

      const dbPayload = { ...updatedCore, metadata: updatedMetadata };
      let locId = editingId;
      if (editingId) { 
        const { error } = await supabase.from('locations').update(dbPayload).eq('id', editingId); 
        if (error) throw error;
      } else { 
        const { data, error } = await supabase.from('locations').insert([dbPayload]).select().single(); 
        if (error) throw error; 
        locId = data.id; 
      }

      if (locId) {
        await supabase.from('location_contacts').delete().eq('location_id', locId);
        if (contactIds && contactIds.length > 0) {
          const contactLinks = contactIds.map(cid => ({ location_id: locId, contact_id: cid }));
          await supabase.from('location_contacts').insert(contactLinks);
        }
      }

      const updatedLoc = { 
        id: locId!, 
        name: updatedCore.name,
        address: updatedCore.address,
        city: updatedCore.city,
        state: updatedCore.state,
        zip: updatedCore.zip,
        type: updatedCore.type,
        image: finalImageUrl,
        zillowUrl: updatedCore.zillow_url,
        squareFeet: updatedCore.square_feet,
        yearBuilt: updatedCore.year_built,
        metadata: updatedMetadata, 
        contactIds: contactIds || [],
        createdAt: createdAt || new Date().toISOString()
      } as Location;
      
      if (editingId) { setLocations(prev => prev.map(l => l.id === editingId ? updatedLoc : l)); }
      else { setLocations(prev => [updatedLoc, ...prev]); }
      setIsSidebarOpen(false);
      setImageFile(null);
    } catch (err) { alert(`Failed to save: ${getErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const handleAddLocationNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.title || !newNote.content || !selectedLocationId) return;
    setSaving(true);
    try {
      const payload = {
        title: newNote.title,
        content: newNote.content,
        entity_type: 'Location',
        entity_id: selectedLocationId,
        category_id: newNote.categoryId
      };
      const { data, error } = await supabase.from('notes').insert([payload]).select().single();
      if (error) throw error;
      setNotes(prev => [{
        id: data.id,
        title: data.title,
        content: data.content,
        createdAt: data.created_at,
        entityType: 'Location',
        entityId: selectedLocationId,
        categoryId: data.category_id
      }, ...prev]);
      setIsAddingNote(false);
      setNewNote({ title: '', content: '', categoryId: categories[0]?.id || '' });
    } catch (err) { alert(`Note creation failed: ${getErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const MetadataChip = ({ icon: Icon, label, value, color = "olive", status = false }: any) => {
    if (!status && (value === undefined || value === null || value === false || value === '' || value === 0)) return null;
    let displayValue = value;
    let iconColorClass = "text-[#606c38]";
    let iconBgClass = "bg-[#606c38]/10";

    if (color === "green") { iconColorClass = "text-emerald-600"; iconBgClass = "bg-emerald-50"; }
    if (color === "red") { iconColorClass = "text-rose-600"; iconBgClass = "bg-rose-50"; }
    if (color === "amber") { iconColorClass = "text-amber-600"; iconBgClass = "bg-amber-50"; }
    if (color === "indigo") { iconColorClass = "text-indigo-600"; iconBgClass = "bg-indigo-50"; }

    if (status) {
      displayValue = value ? 'ACTIVE' : 'OFFLINE';
      iconColorClass = value ? 'text-emerald-600' : 'text-rose-500';
      iconBgClass = value ? 'bg-emerald-50' : 'bg-rose-50';
    } else if (value === true) {
      displayValue = 'Yes';
    }

    return (
      <div className="px-3 py-3 bg-white border border-slate-200 rounded-2xl flex items-center gap-3 shadow-sm hover:shadow-md transition-all group">
        <div className={`${iconBgClass} ${iconColorClass} p-2.5 rounded-xl shrink-0`}>
          <Icon size={14} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-widest leading-none mb-1">{label}</p>
          <p className="text-[11px] font-medium text-slate-800 truncate leading-none uppercase tracking-tight">{displayValue}</p>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, color = "olive" }: any) => {
    let colorClass = "text-[#606c38]";
    if (color === "indigo") colorClass = "text-indigo-600";
    if (color === "amber") colorClass = "text-amber-600";
    if (color === "red") colorClass = "text-rose-600";
    
    return (
      <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-3">
        <div className={`${colorClass} p-2 bg-slate-50 rounded-xl`}><Icon size={16} strokeWidth={2} /></div>
        <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.15em]">{title}</h4>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-50 text-blue-600';
      case 'In Progress': return 'bg-amber-50 text-amber-600';
      case 'Completed': return 'bg-green-50 text-green-600';
      case 'Cancelled': return 'bg-red-50 text-red-600';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const getFullAddress = (p?: Location) => {
    if (!p) return '';
    return `${p.address}, ${p.city}, ${p.state} ${p.zip}`;
  };

  return (
    <div className="relative animate-in fade-in duration-300">
      {selectedLocationId && selectedLocation ? (
        <div className="animate-in slide-in-from-right-2 duration-300 space-y-6 pb-20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedLocationId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronLeft size={20} /></button>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-800 leading-none truncate">{selectedLocation.address || selectedLocation.name}</h3>
                <p className="text-[10px] text-slate-500 font-bold flex items-center gap-1 mt-1 truncate uppercase tracking-widest"><MapPin size={10} /> {selectedLocation.address}, {selectedLocation.city}</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              {selectedLocation.zillowUrl && (
                <button onClick={() => window.open(selectedLocation.zillowUrl, '_blank')} className="flex-1 sm:flex-none px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-all flex items-center justify-center gap-2 border border-blue-100 shadow-sm"><LinkIcon size={12} /> Zillow</button>
              )}
              <button onClick={(e) => handleOpenEdit(selectedLocation, e)} className="flex-1 sm:flex-none px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center justify-center gap-2"><Edit2 size={12} /> Edit</button>
              <button onClick={(e) => handleDeleteLocation(selectedLocation.id, e)} className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"><Trash2 size={16} /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-8">
              <div className="bg-white rounded-[2rem] border border-slate-200 p-6 md:p-8 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#606c38]/5 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative z-10"><p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><Scaling size={12} className="text-[#606c38]"/> Asset Size</p><p className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{selectedLocation.squareFeet?.toLocaleString() || '--'} <span className="text-[10px] font-bold text-slate-300">SF</span></p></div>
                <div className="relative z-10"><p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><Calendar size={12} className="text-indigo-500"/> Built</p><p className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{selectedLocation.yearBuilt || '--'}</p></div>
                <div className="relative z-10"><p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><Bed size={12} className="text-amber-500"/> Layout</p><p className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{selectedLocation.metadata?.bedroomCount || '0'}/{selectedLocation.metadata?.bathroomCount || '0'}</p></div>
                <div className="relative z-10"><p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><User size={12} className="text-emerald-500"/> Status</p><p className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{selectedLocation.metadata?.propertyOccupied ? 'Occupied' : 'Vacant'}</p></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <div className="space-y-10">
                  <section>
                    <SectionHeader icon={Key} title="Security & Entry" color="indigo" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <MetadataChip icon={Lock} label="Lockbox Type" value={selectedLocation.metadata?.lockboxType} />
                      <MetadataChip icon={Hash} label="Entry Code" value={selectedLocation.metadata?.lockboxCode} />
                      <MetadataChip icon={ShieldCheck} label="Alarm System" value={selectedLocation.metadata?.alarmSystemPresent} />
                      <MetadataChip icon={Dog} label="Pets" value={selectedLocation.metadata?.petsPresent} color="amber" />
                      <MetadataChip icon={Box} label="Condo Unit" value={selectedLocation.metadata?.condoUnit} />
                      <MetadataChip icon={ParkingCircle} label="Parking Loc." value={selectedLocation.metadata?.parkingLocation} />
                      <MetadataChip icon={Navigation} label="Gate Access" value={selectedLocation.metadata?.gateAccessRequired} />
                      <MetadataChip icon={Info} label="HOA Access" value={selectedLocation.metadata?.hoaAccessRequired} />
                    </div>
                  </section>

                  <section>
                    <SectionHeader icon={Zap} title="Utilities & HVAC" color="amber" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <MetadataChip icon={Power} label="Electric" value={selectedLocation.metadata?.utilitiesOnElectric} status />
                      <MetadataChip icon={Droplets} label="Water" value={selectedLocation.metadata?.utilitiesOnWater} status />
                      <MetadataChip icon={Wind} label="Gas / Fuel" value={selectedLocation.metadata?.utilitiesOnGas} status />
                      <MetadataChip icon={Thermometer} label="A/C Present" value={selectedLocation.metadata?.acPresent} />
                      <MetadataChip icon={Activity} label="A/C Units" value={selectedLocation.metadata?.acUnitCount} />
                      <MetadataChip icon={GlassWater} label="Water Heaters" value={selectedLocation.metadata?.waterHeaterCount} />
                      <MetadataChip icon={Battery} label="Generator" value={selectedLocation.metadata?.generatorPresent} />
                      <MetadataChip icon={Sun} label="Solar Array" value={selectedLocation.metadata?.solarSystemPresent} />
                    </div>
                  </section>
                </div>

                <div className="space-y-10">
                  <section>
                    <SectionHeader icon={Layers} title="Structural Components" color="olive" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <MetadataChip icon={Warehouse} label="Basement" value={selectedLocation.metadata?.basementPresent} />
                      <MetadataChip icon={HardHat} label="Crawlspace" value={selectedLocation.metadata?.crawlspacePresent} />
                      <MetadataChip icon={ArrowUp} label="Attic Access" value={selectedLocation.metadata?.atticAccessPresent} />
                      <MetadataChip icon={Construction} label="Stories" value={selectedLocation.metadata?.numberOfStories} />
                      <MetadataChip icon={Scaling} label="Roof Type" value={selectedLocation.metadata?.roofMaterial} />
                      <MetadataChip icon={Home} label="Structures" value={selectedLocation.metadata?.attachedStructuresPresent} />
                    </div>
                  </section>

                  <section>
                    <SectionHeader icon={AlertCircle} title="Registry Risks" color="red" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <MetadataChip icon={Waves} label="Water Intrusion" value={selectedLocation.metadata?.knownWaterIntrusionHistory} color="red" />
                      <MetadataChip icon={ShieldCheck} label="Roof Leak History" value={selectedLocation.metadata?.knownRoofLeaksHistory} color="red" />
                      <MetadataChip icon={Mountain} label="Terrain" value={selectedLocation.metadata?.steepTerrainPresent ? 'Steep' : ''} color="amber" />
                      <MetadataChip icon={Flame} label="Fireplace" value={selectedLocation.metadata?.fireplacePresent} color="amber" />
                    </div>
                  </section>
                </div>
              </div>

              <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
                {!viewingNote ? (
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                      <div>
                        <h4 className="text-sm font-black text-slate-800 flex items-center gap-2 uppercase tracking-widest"><MessageSquareText size={18} className="text-[#606c38]" /> Technical Log</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Asset Intelligence History</p>
                      </div>
                      <button onClick={() => setIsAddingNote(!isAddingNote)} className={`w-full sm:w-auto px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${isAddingNote ? 'bg-slate-100 text-slate-500' : 'bg-[#606c38] text-white shadow-lg shadow-[#606c38]/10'}`}>
                        {isAddingNote ? <><X size={14} /> Abort</> : <><Plus size={14} /> New Record</>}
                      </button>
                    </div>

                    {isAddingNote && (
                      <form onSubmit={handleAddLocationNote} className="mb-10 p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-6 animate-in slide-in-from-top-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div><label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Subject</label><input required className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:ring-1 focus:ring-[#606c38] outline-none" value={newNote.title} onChange={(e) => setNewNote({...newNote, title: e.target.value})} placeholder="Title..." /></div>
                          <div><label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Category</label><select className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 outline-none" value={newNote.categoryId} onChange={(e) => setNewNote({...newNote, categoryId: e.target.value})}>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                        </div>
                        <div><label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-2">Content</label><textarea required rows={4} className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-sm font-medium text-slate-600 outline-none resize-none" value={newNote.content} onChange={(e) => setNewNote({...newNote, content: e.target.value})} placeholder="Enter findings..." /></div>
                        <div className="flex justify-end"><button disabled={saving} type="submit" className="w-full sm:w-auto bg-[#606c38] text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl">{saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Persist</button></div>
                      </form>
                    )}

                    <div className="space-y-3">
                      {locationNotes.length > 0 ? locationNotes.map(note => (
                        <div key={note.id} className="p-4 bg-white border border-slate-100 rounded-2xl flex items-center justify-between hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setViewingNote(note)}>
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="p-2 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-[#606c38]/10 group-hover:text-[#606c38] shrink-0"><StickyNote size={16} /></div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h5 className="font-bold text-slate-900 text-sm truncate">{note.title}</h5>
                                <span className="text-[8px] font-black text-[#606c38] bg-[#606c38]/10 px-1.5 py-0.5 rounded uppercase tracking-tighter">{categories.find(c => c.id === note.categoryId)?.name}</span>
                              </div>
                              <p className="text-[11px] text-slate-400 font-medium truncate">{note.content}</p>
                            </div>
                          </div>
                          <Eye size={16} className="text-slate-200 group-hover:text-[#606c38] ml-2 shrink-0" />
                        </div>
                      )) : <div className="py-12 text-center bg-slate-50/50 rounded-[2rem]"><p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No Technical Observations</p></div>}
                    </div>
                  </div>
                ) : (
                  <div className="animate-in fade-in duration-300">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                      <button onClick={() => setViewingNote(null)} className="p-2 hover:bg-white rounded-xl text-slate-500 flex items-center gap-2 font-black text-[10px] uppercase tracking-widest"><ArrowLeft size={16} /> Back</button>
                      <h4 className="text-xs font-bold text-slate-900 uppercase truncate px-4">{viewingNote.title}</h4>
                      <button onClick={() => setViewingNote(null)} className="p-2 text-slate-400 hover:text-slate-600"><X size={20} /></button>
                    </div>
                    <div className="p-6 md:p-10">
                       <div className="p-6 md:p-10 bg-slate-50 rounded-[2rem] border border-slate-100"><p className="text-slate-700 text-sm md:text-base font-medium leading-relaxed whitespace-pre-wrap">{viewingNote.content}</p></div>
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="space-y-6">
              <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center gap-2"><ClipboardList size={16} className="text-[#606c38]" /><h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Registry Timeline</h4></div>
                <div className="divide-y divide-slate-100">
                  {associatedInspections.length > 0 ? associatedInspections.map(ins => (
                    <button key={ins.id} onClick={() => onNavigateToInspection?.(ins.id)} className="w-full text-left p-5 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-900 mb-1">{ins.date}</p>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter ${getStatusColor(ins.status)}`}>{ins.status}</span>
                      </div>
                      <ChevronRight size={14} className="text-slate-300" />
                    </button>
                  )) : <div className="p-8 text-center text-[10px] font-black text-slate-300 uppercase">No Prior Records</div>}
                </div>
              </section>

              <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center gap-2"><Users size={16} className="text-[#606c38]" /><h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Stakeholders</h4></div>
                <div className="divide-y divide-slate-100">
                  {associatedContacts.length > 0 ? associatedContacts.map(contact => (
                    <div key={contact.id} className="p-5 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 shadow-sm"><User size={20} strokeWidth={2} /></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-900 truncate mb-1">{contact.name}</p>
                        <p className="text-[10px] font-bold text-slate-400 truncate uppercase tracking-tighter">{contact.email}</p>
                      </div>
                    </div>
                  )) : <div className="p-8 text-center text-[10px] font-black text-slate-300 uppercase">None Linked</div>}
                </div>
              </section>

              {/* Cover Image Card with Direct Actions */}
              <div className="rounded-[2rem] overflow-hidden border border-slate-200 shadow-sm group bg-white">
                <div className="relative aspect-video overflow-hidden">
                  {selectedLocation.image ? (
                    <>
                      <img src={selectedLocation.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Asset Photo" />
                      {updatingDirectImage && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center backdrop-blur-[2px]">
                          <Loader2 className="animate-spin text-[#606c38]" size={24} />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-slate-50 flex flex-col items-center justify-center text-slate-300">
                      <ImageIcon size={48} strokeWidth={1} />
                      <p className="text-[10px] font-black uppercase mt-2">No Cover Artifact</p>
                      {updatingDirectImage && <Loader2 className="animate-spin text-[#606c38] mt-4" size={18} />}
                    </div>
                  )}
                </div>
                <div className="p-4 flex justify-between items-center bg-white border-t border-slate-100">
                   <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Media Archive</span>
                   <div className="flex gap-2">
                      <button 
                        onClick={() => directFileInputRef.current?.click()}
                        disabled={updatingDirectImage}
                        className="p-2 hover:bg-slate-50 text-slate-400 hover:text-blue-600 transition-all rounded-lg"
                        title="Update Image"
                      >
                        <Camera size={16} />
                      </button>
                      {selectedLocation.image && (
                        <>
                          <button 
                            onClick={handleDirectImageDelete}
                            disabled={updatingDirectImage}
                            className="p-2 hover:bg-slate-50 text-slate-400 hover:text-rose-600 transition-all rounded-lg"
                            title="Remove Image"
                          >
                            <Trash2 size={16} />
                          </button>
                          <button 
                            onClick={() => window.open(selectedLocation.image, '_blank')} 
                            className="p-2 hover:bg-slate-50 text-slate-400 hover:text-[#606c38] transition-all rounded-lg"
                            title="View Full Resolution"
                          >
                            <ExternalLink size={16} />
                          </button>
                        </>
                      )}
                   </div>
                   <input ref={directFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleDirectImageUpdate} />
                </div>
              </div>

              <div className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-slate-200">
                <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center"><h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2"><MapPin size={14} className="text-[#606c38]" /> Geo-Reference</h4></div>
                <div className="relative h-[250px] md:h-[300px] w-full bg-slate-100">
                  <iframe width="100%" height="100%" frameBorder="0" className="contrast-[1.05] grayscale-[0.2]" src={`https://maps.google.com/maps?q=${encodeURIComponent(getFullAddress(selectedLocation))}&t=m&z=17&output=embed`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div><h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">Registry</h3><p className="text-sm text-slate-500 font-medium mt-0.5">Asset configuration index.</p></div>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search address..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold focus:ring-1 focus:ring-[#606c38] outline-none shadow-sm transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
              <button onClick={handleOpenAdd} className="w-full sm:w-auto bg-[#606c38] text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-[#283618] transition-all flex items-center justify-center gap-2"><Plus size={18} /> Register Asset</button>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto no-scrollbar">
            <table className="w-full text-left min-w-max">
              <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Asset Record</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Type</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Details</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLocations.map((loc) => (
                  <tr key={loc.id} onClick={() => setSelectedLocationId(loc.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                    <td className="px-6 py-5"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 shadow-sm overflow-hidden">{loc.image ? <img src={loc.image} className="w-full h-full object-cover" /> : <Building size={20} />}</div><div className="min-w-0"><p className="text-sm font-bold text-slate-800 truncate max-w-[250px]">{loc.address || loc.name}</p><p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{loc.city}, {loc.state}</p></div></div></td>
                    <td className="px-6 py-5"><span className="text-[9px] font-black text-[#606c38] bg-[#606c38]/5 px-2 py-0.5 rounded-md uppercase tracking-wider">{loc.type}</span></td>
                    <td className="px-6 py-5 text-right"><p className="text-xs font-bold text-slate-700">{loc.squareFeet?.toLocaleString()} <span className="text-[9px] text-slate-400">SF</span></p><p className="text-[10px] text-slate-400 font-bold uppercase">Built {loc.yearBuilt}</p></td>
                    <td className="px-6 py-5 text-right"><div className="flex items-center justify-end gap-2"><button onClick={(e) => handleDeleteLocation(loc.id, e)} className="p-2 text-slate-300 hover:text-rose-500 transition-all"><Trash2 size={18} /></button><ChevronRight size={18} className="text-slate-200" /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-200" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full md:max-w-5xl bg-white shadow-2xl transition-transform duration-500 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="px-6 py-4 md:px-8 md:py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Technical Record' : 'Register New Asset'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-xl text-slate-400 transition-all"><X size={24} /></button>
          </div>
          
          <form onSubmit={handleSaveLocation} className="flex-1 overflow-y-auto p-6 md:p-10 space-y-12 no-scrollbar bg-white">
            <section className="space-y-8">
              <SectionHeader icon={Info} title="Asset Identity" color="olive" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="md:col-span-1 space-y-4">
                  <div onClick={() => fileInputRef.current?.click()} className="aspect-video md:aspect-square bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-all overflow-hidden relative group">
                    {formData.image ? <img src={formData.image} className="w-full h-full object-cover" /> : <div className="text-center p-4"><Upload size={24} className="mx-auto mb-2 text-slate-300" /><p className="text-[9px] font-black text-slate-400 uppercase">Upload Media</p></div>}
                    <div className="absolute bottom-4 right-4 bg-[#606c38] text-white p-2 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"><CloudUpload size={14} /></div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  </div>
                  <FormField label="Type" type="select" options={[{value:'Property', label:'Residential Property'}, {value:'Business', label:'Commercial Business'}]} value={formData.type} onChange={(v: string)=>setFormData(prev => ({...prev, type: v as any}))} />
                  <div className="grid grid-cols-2 gap-2">
                    <FormField label="Status" type="checkbox" placeholder="Occupied" value={formData.propertyOccupied} onChange={(v: boolean)=>setFormData(prev => ({...prev, propertyOccupied: v}))} />
                    <FormField label="Status" type="checkbox" placeholder="Vacant" value={formData.vacant} onChange={(v: boolean)=>setFormData(prev => ({...prev, vacant: v}))} />
                  </div>
                </div>
                <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="sm:col-span-2 md:col-span-3"><FormField label="Full Street Address" placeholder="123 Street Ave" value={formData.address} onChange={(v: string)=>setFormData(prev => ({...prev, address: v}))} /></div>
                  <FormField label="City" value={formData.city} onChange={(v: string)=>setFormData(prev => ({...prev, city: v}))} />
                  <FormField label="State" value={formData.state} onChange={(v: string)=>setFormData(prev => ({...prev, state: v}))} />
                  <FormField label="Zip" value={formData.zip} onChange={(v: string)=>setFormData(prev => ({...prev, zip: v}))} />
                  <FormField label="Area (SF)" type="number" value={formData.squareFeet} onChange={(v: any)=>setFormData(prev => ({...prev, squareFeet: v}))} />
                  <FormField label="Year Built" type="number" value={formData.yearBuilt} onChange={(v: any)=>setFormData(prev => ({...prev, yearBuilt: v}))} />
                  <div className="sm:col-span-2 md:col-span-3"><FormField label="Zillow Registry URL" placeholder="https://www.zillow.com/homedetails/..." value={formData.zillowUrl} onChange={(v: string)=>setFormData(prev => ({...prev, zillowUrl: v}))} /></div>
                  {formData.type === 'Property' && (
                     <FormField label="Residential Sub-Type" type="select" options={[{value:'Single-family', label:'Single-family'}, {value:'Condo', label:'Condo'}, {value:'Townhome', label:'Townhome'}, {value:'Multi-family', label:'Multi-family'}]} value={formData.propertyTypeSub} onChange={(v: any)=>setFormData(prev => ({...prev, propertyTypeSub: v}))} />
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-8">
              <SectionHeader icon={Users} title="Stakeholder Association" color="olive" />
              <div className="bg-slate-50 rounded-[2rem] border border-slate-200 p-6">
                <div className="relative mb-6"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search stakeholders..." className="w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold outline-none shadow-sm" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto no-scrollbar">
                  {contacts.filter(c => c.name.toLowerCase().includes(contactSearch.toLowerCase())).map(contact => {
                    const isSelected = formData.contactIds?.includes(contact.id);
                    return (
                      <button key={contact.id} type="button" onClick={() => toggleContactAssociation(contact.id)} className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left group ${isSelected ? 'bg-[#606c38] border-[#606c38] shadow-lg scale-[1.02]' : 'bg-white border-slate-200'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-50 text-slate-400'}`}>{isSelected ? <Check size={14} /> : <User size={14} />}</div>
                        <div className="min-w-0"><p className={`text-[11px] font-bold truncate ${isSelected ? 'text-white' : 'text-slate-800'}`}>{contact.name}</p><p className={`text-[8px] font-bold truncate uppercase ${isSelected ? 'text-white/60' : 'text-slate-400'}`}>{contact.company || 'Private'}</p></div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {formData.type === 'Property' && (
              <>
                <section className="space-y-8">
                  <SectionHeader icon={Home} title="Structural & Layout" color="indigo" />
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                    <FormField label="Stories" type="number" value={formData.numberOfStories} onChange={(v: any)=>setFormData(prev => ({...prev, numberOfStories: v}))} />
                    <FormField label="Total Rooms" type="number" value={formData.totalRooms} onChange={(v: any)=>setFormData(prev => ({...prev, totalRooms: v}))} />
                    <FormField label="Bedrooms" type="number" value={formData.bedroomCount} onChange={(v: any)=>setFormData(prev => ({...prev, bedroomCount: v}))} />
                    <FormField label="Bathrooms" type="number" value={formData.bathroomCount} onChange={(v: any)=>setFormData(prev => ({...prev, bathroomCount: v}))} />
                    <FormField label="Half Baths" type="number" value={formData.halfBathCount} onChange={(v: any)=>setFormData(prev => ({...prev, halfBathCount: v}))} />
                    <FormField label="Kitchens" type="number" value={formData.kitchenCount} onChange={(v: any)=>setFormData(prev => ({...prev, kitchenCount: v}))} />
                    <FormField label="Construction" placeholder="e.g. CBS, Frame" value={formData.constructionStyle} onChange={(v: string)=>setFormData(prev => ({...prev, constructionStyle: v}))} className="col-span-2 lg:col-span-2" />
                    <FormField label="Garage Spaces" type="number" value={formData.garageSpaces} onChange={(v: any)=>setFormData(prev => ({...prev, garageSpaces: v}))} />
                    <FormField label="Garage Present" type="checkbox" value={formData.garagePresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, garagePresent: v}))} />
                    <FormField label="Basement" type="checkbox" value={formData.basementPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, basementPresent: v}))} />
                    <FormField label="Crawlspace" type="checkbox" value={formData.crawlspacePresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, crawlspacePresent: v}))} />
                    <FormField label="Attic Access" type="checkbox" value={formData.atticAccessPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, atticAccessPresent: v}))} />
                    <FormField label="Laundry Rm" type="checkbox" value={formData.laundryRoomPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, laundryRoomPresent: v}))} />
                    <FormField label="Utility Rm" type="checkbox" value={formData.utilityRoomPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, utilityRoomPresent: v}))} />
                    <FormField label="Structures" type="checkbox" placeholder="Attached structures" value={formData.attachedStructuresPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, attachedStructuresPresent: v}))} />
                  </div>
                </section>

                <section className="space-y-8">
                  <SectionHeader icon={Zap} title="Systems & Utilities" color="amber" />
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                    <FormField label="Supply" type="select" options={[{value:'City', label:'City'}, {value:'Well', label:'Well'}]} value={formData.waterSupplyType} onChange={(v: any)=>setFormData(prev => ({...prev, waterSupplyType: v}))} />
                    <FormField label="Sewer" type="select" options={[{value:'City', label:'City'}, {value:'Septic', label:'Septic'}]} value={formData.sewerType} onChange={(v: any)=>setFormData(prev => ({...prev, sewerType: v}))} />
                    <FormField label="A/C Present" type="checkbox" value={formData.acPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, acPresent: v}))} />
                    <FormField label="A/C Units" type="number" value={formData.acUnitCount} onChange={(v: any)=>setFormData(prev => ({...prev, acUnitCount: v}))} />
                    <FormField label="Zoned System" type="checkbox" value={formData.zonedSystem} onChange={(v: boolean)=>setFormData(prev => ({...prev, zonedSystem: v}))} />
                    <FormField label="Mini-Split" type="checkbox" value={formData.miniSplitPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, miniSplitPresent: v}))} />
                    <FormField label="Electric On" type="checkbox" value={formData.utilitiesOnElectric} onChange={(v: boolean)=>setFormData(prev => ({...prev, utilitiesOnElectric: v}))} />
                    <FormField label="Water On" type="checkbox" value={formData.utilitiesOnWater} onChange={(v: boolean)=>setFormData(prev => ({...prev, utilitiesOnWater: v}))} />
                    <FormField label="Fuel On" type="checkbox" value={formData.utilitiesOnGas} onChange={(v: boolean)=>setFormData(prev => ({...prev, utilitiesOnGas: v}))} />
                    <FormField label="Solar Array" type="checkbox" value={formData.solarSystemPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, solarSystemPresent: v}))} />
                    <FormField label="Generator" type="checkbox" value={formData.generatorPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, generatorPresent: v}))} />
                    <FormField label="Water Heaters" type="number" value={formData.waterHeaterCount} onChange={(v: any)=>setFormData(prev => ({...prev, waterHeaterCount: v}))} />
                    <FormField label="Tankless" type="checkbox" value={formData.tanklessWaterHeaterPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, tanklessWaterHeaterPresent: v}))} />
                    <FormField label="Softener" type="checkbox" value={formData.waterSoftenerPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, waterSoftenerPresent: v}))} />
                    <FormField label="Subpanel" type="checkbox" value={formData.subpanelPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, subpanelPresent: v}))} />
                  </div>
                </section>

                <section className="space-y-8">
                  <SectionHeader icon={Compass} title="Substructure & Roof" color="indigo" />
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                    <FormField label="Roof Material" value={formData.roofMaterial} onChange={(v: string)=>setFormData(prev => ({...prev, roofMaterial: v}))} className="col-span-2 lg:col-span-2" />
                    <FormField label="Roof Height" placeholder="Estimate ft." value={formData.roofHeightEstimate} onChange={(v: string)=>setFormData(prev => ({...prev, roofHeightEstimate: v}))} />
                    <FormField label="Roof Access" type="checkbox" value={formData.roofAccessible} onChange={(v: boolean)=>setFormData(prev => ({...prev, roofAccessible: v}))} />
                    <FormField label="Flat Sections" type="checkbox" value={formData.flatRoofSectionsPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, flatRoofSectionsPresent: v}))} />
                    <FormField label="Secondary Roof" type="checkbox" value={formData.secondaryRoofStructures} onChange={(v: boolean)=>setFormData(prev => ({...prev, secondaryRoofStructures: v}))} />
                    <FormField label="Crawl Accessible" type="checkbox" value={formData.crawlspaceAccessible} onChange={(v: boolean)=>setFormData(prev => ({...prev, crawlspaceAccessible: v}))} />
                    <FormField label="Low Clearance" type="checkbox" value={formData.lowClearanceAreas} onChange={(v: boolean)=>setFormData(prev => ({...prev, lowClearanceAreas: v}))} />
                    <FormField label="Roof Leak Hist." type="checkbox" value={formData.knownRoofLeaksHistory} onChange={(v: boolean)=>setFormData(prev => ({...prev, knownRoofLeaksHistory: v}))} />
                    <FormField label="Water Intrusion" type="checkbox" value={formData.knownWaterIntrusionHistory} onChange={(v: boolean)=>setFormData(prev => ({...prev, knownWaterIntrusionHistory: v}))} />
                    <FormField label="Attic Location" placeholder="Access point..." value={formData.atticAccessLocation} onChange={(v: string)=>setFormData(prev => ({...prev, atticAccessLocation: v}))} className="col-span-2 lg:col-span-2" />
                  </div>
                </section>

                <section className="space-y-8">
                  <SectionHeader icon={Trees} title="Exterior & Amenities" color="olive" />
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                    <FormField label="Pool" type="checkbox" value={formData.poolPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, poolPresent: v}))} />
                    <FormField label="Spa" type="checkbox" value={formData.spaPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, spaPresent: v}))} />
                    <FormField label="Gated Yard" type="checkbox" value={formData.gatedBackyard} onChange={(v: boolean)=>setFormData(prev => ({...prev, gatedBackyard: v}))} />
                    <FormField label="Fenced Yard" type="checkbox" value={formData.fencedYard} onChange={(v: boolean)=>setFormData(prev => ({...prev, fencedYard: v}))} />
                    <FormField label="Fireplace" type="checkbox" value={formData.fireplacePresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, fireplacePresent: v}))} />
                    <FormField label="Outdoor Kitchen" type="checkbox" value={formData.outdoorKitchenPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, outdoorKitchenPresent: v}))} />
                    <FormField label="Elevator" type="checkbox" value={formData.elevatorPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, elevatorPresent: v}))} />
                    <FormField label="Dock" type="checkbox" value={formData.dockPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, dockPresent: v}))} />
                    <FormField label="Seawall" type="checkbox" value={formData.seawallPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, seawallPresent: v}))} />
                    <FormField label="Sprinklers" type="checkbox" value={formData.sprinklerSystemPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, sprinklerSystemPresent: v}))} />
                    <FormField label="Steep Terrain" type="checkbox" value={formData.steepTerrainPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, steepTerrainPresent: v}))} />
                    <FormField label="Elevation (ft)" type="number" value={formData.elevation} onChange={(v: any)=>setFormData(prev => ({...prev, elevation: v}))} />
                  </div>
                </section>
              </>
            )}

            <section className="space-y-8">
              <SectionHeader icon={Lock} title="Access & Security" color="indigo" />
              <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                <FormField label="Lockbox Type" value={formData.lockboxType} onChange={(v: string)=>setFormData(prev => ({...prev, lockboxType: v}))} />
                <FormField label="Entry Code" value={formData.lockboxCode} onChange={(v: string)=>setFormData(prev => ({...prev, lockboxCode: v}))} />
                <FormField label="Alarm Present" type="checkbox" value={formData.alarmSystemPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, alarmSystemPresent: v}))} />
                <FormField label="Alarm Code" value={formData.alarmCodeProvided} onChange={(v: string)=>setFormData(prev => ({...prev, alarmCodeProvided: v}))} />
                <FormField label="Gate Required" type="checkbox" value={formData.gateAccessRequired} onChange={(v: boolean)=>setFormData(prev => ({...prev, gateAccessRequired: v}))} />
                <FormField label="HOA Required" type="checkbox" value={formData.hoaAccessRequired} onChange={(v: boolean)=>setFormData(prev => ({...prev, hoaAccessRequired: v}))} />
                <FormField label="Common Areas" type="checkbox" placeholder="Common Inspection Allowed" value={formData.commonAreaInspectionAllowed} onChange={(v: boolean)=>setFormData(prev => ({...prev, commonAreaInspectionAllowed: v}))} />
                {formData.type === 'Property' && (
                   <>
                    <FormField label="Pets Present" type="checkbox" value={formData.petsPresent} onChange={(v: boolean)=>setFormData(prev => ({...prev, petsPresent: v}))} />
                    <FormField label="Pets Secured" type="checkbox" value={formData.petsSecured} onChange={(v: boolean)=>setFormData(prev => ({...prev, petsSecured: v}))} />
                   </>
                )}
                <FormField label="Parking Loc." value={formData.parkingLocation} onChange={(v: string)=>setFormData(prev => ({...prev, parkingLocation: v}))} />
                <FormField label="Condo Unit" value={formData.condoUnit} onChange={(v: string)=>setFormData(prev => ({...prev, condoUnit: v}))} />
                <FormField label="Floor #" value={formData.floorNumber} onChange={(v: string)=>setFormData(prev => ({...prev, floorNumber: v}))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-50">
                <FormField label="Access Contact" placeholder="Name..." value={formData.accessContactName} onChange={(v: string)=>setFormData(prev => ({...prev, accessContactName: v}))} />
                <FormField label="Access Phone" placeholder="Phone..." value={formData.accessContactPhone} onChange={(v: string)=>setFormData(prev => ({...prev, accessContactPhone: v}))} />
                <FormField label="Parking Res." placeholder="Restrictions..." value={formData.parkingRestrictions} onChange={(v: string)=>setFormData(prev => ({...prev, parkingRestrictions: v}))} />
                <div className="md:col-span-3"><FormField label="HOA Restrictions" type="textarea" placeholder="Note any specific HOA rules..." value={formData.hoaRestrictions} onChange={(v: string)=>setFormData(prev => ({...prev, hoaRestrictions: v}))} /></div>
                <div className="md:col-span-3"><FormField label="Access Instructions" type="textarea" placeholder="Detailed arrival logic..." value={formData.accessInstructions} onChange={(v: string)=>setFormData(prev => ({...prev, accessInstructions: v}))} /></div>
              </div>
            </section>
          </form>

          <div className="p-6 md:p-8 border-t border-slate-100 flex flex-col sm:flex-row gap-4 bg-white shadow-inner">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="w-full sm:flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-2xl border border-slate-200 transition-all">Abort</button>
            <button disabled={saving} type="submit" onClick={handleSaveLocation} className="w-full sm:flex-[2] py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-xl">{saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Commit Record</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Locations;